import {
  Box,
  ClickAwayListener,
  CloseIcon,
  IconButton,
  Input,
  Paper,
  SearchIcon,
  Theme,
  useMediaQuery,
  useTheme,
} from '@freighthub/silhouette-core'
import { ITextSearchResults, SearchService } from 'features/ShipmentsList'
import { inject } from 'mobx-react'
import { ChangeEvent, FC, KeyboardEvent, useRef, useState } from 'react'
import { AnalyticsService } from 'service/AnalyticsService'
import { ErrorService } from 'service/ErrorService'
import { RootStore } from 'service/RootStore'
import { useDebouncedCallback } from 'service/useDebounce'
import { useI18n } from 'utils/localisation'

import { SEARCH_CATEGORIES } from './Search'
import { getHoverColor } from './TopBarHoverStlyes'
import { TopBarSearchResults } from './TopBarSearchResults'

export const testIds = {
  input: 'top-bar-search-input',
  clearButton: 'top-bar-search-clear',
  menu: 'top-bar-search-menu',
}

const trackingIds = {
  input: 'global-header-search_box-input',
  clearButton: 'global-header-search_box-clear',
  menu: 'global-header-search_box_results',
}

export const MIN_SEARCH_TEXT_LENGTH = 3
const MAX_SEARCH_RESULT_COUNT = 3 // for each category
const SEARCH_DEBOUNCE_INTERVAL = 300 // milliseconds

const getStyles = (theme: Theme) => ({
  root: {
    position: 'relative',
    flex: 'none',
    margin: theme.spacing(0, 1),
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      flex: 1,
    },
  },
  iconButton: {
    padding: 0,
  },
  inputRoot: {
    height: 30,
    width: 550,
    boxSizing: 'border-box',
    alignItems: 'center',
    borderRadius: 1,
    padding: theme.spacing(0, 1, 0, 1.5),
    backgroundColor: getHoverColor(theme),
    color: theme.palette.primary.contrastText,
    transition: 'all 250ms ease',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    '& button': {
      color: 'inherit',
    },
    '& input': {
      height: 'inherit',
      '&::-webkit-search-decoration, &::-webkit-search-cancel-button': {
        display: 'none',
      },
    },
  },
  inputRootFocused: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    '& button:hover, & button:focus': {
      backgroundColor: theme.palette.action.hover,
    },
    '& button:disabled': {
      color: theme.palette.action.disabled,
    },
  },
  menu: {
    position: 'absolute',
    left: 0,
    right: 0,
    minHeight: 50,
    marginTop: theme.spacing(1),
    zIndex: theme.zIndex.modal,
  },
  highlighted: {
    color: theme.palette.text.primary,
  },
})

interface TopBarSearchInject {
  searchService: SearchService
  errorService: ErrorService
  analyticsService: AnalyticsService
}

interface TopBarSearchProps extends Partial<TopBarSearchInject> {}

const LeanTopBarSearch: FC<TopBarSearchProps> = ({ searchService, errorService, analyticsService }) => {
  const { translate } = useI18n()
  const theme = useTheme<Theme>()
  const isTinyScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const styles = getStyles(theme)
  const inputRef = useRef(null)
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [results, setResults] = useState<ITextSearchResults>({
    shipments: [],
    messages: [],
  })

  const search = async (inputValue: string): Promise<void> => {
    if (inputValue.length < MIN_SEARCH_TEXT_LENGTH) {
      return
    }

    try {
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      const results: ITextSearchResults = await searchService.textSearch(inputValue, {
        size: MAX_SEARCH_RESULT_COUNT,
        types: SEARCH_CATEGORIES,
      })
      setResults(results)
    } catch (error) {
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      errorService.showErrorDialog(error)
    } finally {
      setIsLoading(false)
    }
  }

  const [loadOptions] = useDebouncedCallback(search, SEARCH_DEBOUNCE_INTERVAL)

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    setInputValue(newValue)
    setIsLoading(true)
    loadOptions(newValue)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && inputValue.length >= MIN_SEARCH_TEXT_LENGTH) {
      setIsLoading(true)
      loadOptions(inputValue)
    } else if (event.key === 'Escape' && inputValue.length === 0) {
      setIsInputFocused(false)
      // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
      inputRef.current.blur()
    }
  }

  const handleInputFocus = () => {
    if (!isInputFocused) {
      setIsInputFocused(true)
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      analyticsService.sendEvent('header-search-focus')
    }
  }

  const handleClickClear = () => {
    setInputValue('')
    // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
    inputRef.current.focus()
  }

  const handleClickAway = () => {
    if (isInputFocused) {
      setIsInputFocused(false)
    }
  }

  const handleClickItem = () => {
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    analyticsService.sendEvent('header-search-click-item')
    handleClickAway()
  }

  const isMenuOpen = isInputFocused && inputValue.length >= MIN_SEARCH_TEXT_LENGTH

  return (
    <Box sx={styles.root} data-userlane-id="header-searchbar">
      <ClickAwayListener onClickAway={handleClickAway}>
        <Input
          sx={{ ...styles.inputRoot, ...(isInputFocused && styles.inputRootFocused) }}
          inputRef={inputRef}
          type="search"
          placeholder={isTinyScreen ? translate('search-placeholder-mobile') : translate('search-placeholder')}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          endAdornment={
            inputValue.length > 0 ? (
              <IconButton
                onClick={handleClickClear}
                sx={styles.iconButton}
                data-testid={testIds.clearButton}
                data-tracking-id={trackingIds.clearButton}
              >
                <CloseIcon />
              </IconButton>
            ) : (
              <SearchIcon fontSize="small" />
            )
          }
          inputProps={{
            'data-testid': testIds.input,
            'data-tracking-id': trackingIds.input,
          }}
          disableUnderline
        />
      </ClickAwayListener>
      {isMenuOpen && (
        <Paper sx={styles.menu} data-testid={testIds.menu} data-tracking-id={trackingIds.menu}>
          {results && <TopBarSearchResults isLoading={isLoading} results={results} onClickItem={handleClickItem} />}
        </Paper>
      )}
    </Box>
  )
}

const injector = inject(
  (ctx: RootStore): TopBarSearchInject => ({
    searchService: ctx.searchService,
    errorService: ctx.errorService,
    analyticsService: ctx.analyticsService,
  })
)

export const TopBarSearch = injector(LeanTopBarSearch)
